import { BlurFilter, Container, Graphics, Sprite, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { SECTORS } from '@/config/sectors';
import { getFileTexture, preloadFileTexture } from '@/assets/resolver';
import { clearSlot, loadSlot } from '@/core/save';
import { t, type I18nKey } from '@/i18n';
import { getActiveDevice } from '@/input/active-device';
import type { InputState } from '@/input/types';
import { sfx } from '@/audio/sfx';
import { ConfirmDialog } from './confirm-dialog';
import { ACH_LAYOUT, buildAchievementsPanel } from './title-achievements';
import { TitleMenus, type Item, type Mode, type TitleCallbacks } from './title-items';
import { buildTitleArt } from './title-rows';
import { SECTOR_LAYOUT } from './title-sectors';
import { bodyText, label } from './widgets';

export type { TitleCallbacks } from './title-items';

const ROW_STEP = 74;
const MAIN_TOP = 0.46;
/** Main rows squeeze so the last one (56 px) clears the bottom edge */
const MAIN_BOTTOM = 0.955;
const MAIN_ROW_HALF = 28;

/**
 * Title flow (docs/01, docs/12 J1): PLAY → SECTOR SELECT (locked/open
 * cards + start weapon) → slot picker · CONTINUE → occupied slots ·
 * ENDLESS (after the campaign) → slot picker · ACHIEVEMENTS · SETTINGS.
 */
export class TitleScreen {
  readonly view = new Container();
  private readonly bg = new Sprite();
  private readonly blur = new BlurFilter({ strength: 5 });
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  private readonly confirm = new ConfirmDialog();
  open = false;
  private mode: Mode = 'main';
  private selected = 0;
  private items: Item[] = [];
  /** Built item nodes in item order (pointer hit → item index) */
  private nodes: Container[] = [];
  private prevMoveX = 0;
  private prevMoveY = 0;
  private screenW = 0;
  private screenH = 0;
  private readonly menus: TitleMenus;

  constructor(callbacks: TitleCallbacks) {
    this.menus = new TitleMenus({
      screenW: () => this.screenW,
      mode: () => this.mode,
      setMode: (mode, selected) => this.setMode(mode, selected),
      rebuild: () => this.rebuild(),
      interactive: (node) => this.makeInteractive(node),
    }, callbacks);
    this.view.visible = false;
    this.bg.visible = false;
    this.view.addChild(this.bg, this.dim, this.layer, this.confirm.view);
  }

  /**
   * Open on the MAIN menu — never in a leftover submenu (after death → MAIN
   * MENU the NEW GAME picker used to reopen and one A/Space overwrote slot 1).
   */
  show(screenW: number, screenH: number): void {
    this.mode = 'main';
    this.selected = 0;
    this.relayout(screenW, screenH);
  }

  /** Refit to a new canvas size, keeping the open submenu (resizes). */
  relayout(screenW: number, screenH: number): void {
    this.open = true;
    this.view.visible = true;
    this.screenW = screenW;
    this.screenH = screenH;
    for (const key of ['ui/title-bg', 'ui/title-avatar'] as const) {
      if (getFileTexture(key) === null) {
        void preloadFileTexture(key).then(() => {
          if (this.open) this.rebuild();
        });
      }
    }
    this.rebuild();
  }

  private setMode(mode: Mode, selected = 0): void {
    this.mode = mode;
    this.selected = selected;
    this.rebuild();
  }

  private layoutBg(): void {
    const tex = getFileTexture('ui/title-bg');
    if (!tex) return;
    this.bg.texture = tex;
    this.bg.visible = true;
    // Key art stays SHARP on the main menu; submenus blur it (user note)
    this.bg.filters = this.mode === 'main' ? [] : [this.blur];
    const scale = Math.max(this.screenW / tex.width, this.screenH / tex.height);
    this.bg.scale.set(scale);
    this.bg.position.set((this.screenW - tex.width * scale) / 2, (this.screenH - tex.height * scale) / 2);
  }

  private rebuild(): void {
    const { screenW, screenH } = this;
    this.layoutBg();
    const dimAlpha = !this.bg.visible ? 0.88 : this.mode === 'main' ? 0.30 : 0.62;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: dimAlpha });
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));
    if (this.mode === 'sectors') return this.buildSectors();
    if (this.mode === 'achievements') return this.buildAchievements();

    this.items = this.mode === 'main' ? this.menus.mainItems() : this.menus.pickerItems();
    let top = screenH * 0.26;
    let step = ROW_STEP;
    if (this.mode === 'main') {
      this.layer.addChild(buildTitleArt(screenW, screenH));
      top = screenH * MAIN_TOP;
      const room = screenH * MAIN_BOTTOM - MAIN_ROW_HALF - top;
      step = Math.min(ROW_STEP, room / Math.max(1, this.items.length - 1));
    } else {
      const head = label(this.menus.pickerTitle(), 24, COLORS.playerYellow, 4);
      head.anchor.set(0.5);
      head.position.set(screenW / 2, screenH * 0.16);
      const hint = label(t('title.deleteHint'), 13, 0x6a7076, 2);
      hint.anchor.set(0.5);
      hint.position.set(screenW / 2, screenH * 0.16 + 34);
      this.layer.addChild(head, hint);
    }
    this.nodes = this.items.map((item, i) => item.build(i === this.selected, top + i * step));
    this.layer.addChild(...this.nodes);
  }

  /** A design-space root scaled to fit the canvas (sector select, panels). */
  private designRoot(w: number, h: number): Container {
    const root = new Container();
    const scale = Math.min(1, (this.screenW - 40) / w, (this.screenH - 24) / h);
    root.scale.set(scale);
    root.position.set((this.screenW - w * scale) / 2, (this.screenH - h * scale) / 2);
    this.layer.addChild(root);
    return root;
  }

  private buildSectors(): void {
    const L = SECTOR_LAYOUT;
    const root = this.designRoot(L.w, L.h);
    const head = label(t('sector.select'), 26, COLORS.playerYellow, 4);
    head.anchor.set(0.5, 0);
    head.position.set(L.w / 2, 0);
    root.addChild(head);
    this.items = this.menus.sectorItems();
    this.nodes = this.items.map((item, i) => item.build(i === this.selected, L.backY));
    root.addChild(...this.nodes);
    const device = getActiveDevice();
    const hintKey: I18nKey = device === 'gamepad' ? 'sector.hint.gamepad'
      : device === 'touch' ? 'sector.hint.touch' : 'sector.hint.keyboard';
    const hint = bodyText(t(hintKey), 13, 0x6a7076);
    hint.anchor.set(0.5, 1);
    hint.position.set(L.w / 2, L.hintY);
    root.addChild(hint);
  }

  private buildAchievements(): void {
    const L = ACH_LAYOUT;
    const root = this.designRoot(L.w, L.h);
    root.addChild(buildAchievementsPanel());
    this.items = [this.menus.backItem(L.w / 2)];
    this.nodes = [this.items[0]!.build(true, L.backY)];
    root.addChild(...this.nodes);
  }

  private makeInteractive(node: Container): Container {
    node.eventMode = 'static';
    node.cursor = 'pointer';
    // Hover-focus is mouse-only: on touch the rebuild ate the tap (wave 4 rule)
    node.on('pointerover', (e: FederatedPointerEvent) => {
      if (e.pointerType === 'mouse') this.select(this.nodes.indexOf(node));
    });
    node.on('pointertap', () => {
      const i = this.nodes.indexOf(node);
      if (i < 0) return;
      this.select(i);
      this.activate();
    });
    return node;
  }

  private select(i: number): void {
    if (i < 0 || i === this.selected) return;
    this.selected = i;
    if (this.mode === 'sectors' && i < SECTORS.length) this.menus.sectorFocus = i;
    this.rebuild();
  }

  private activate(): void {
    sfx.uiConfirm();
    this.items[this.selected]?.run();
  }

  /**
   * Stick/arrow step. The sector select is a grid: ←/→ walk the cards (or
   * cycle the start weapon), ↑/↓ go cards → weapon → back.
   */
  private navigate(dx: number, dy: number): void {
    let next = this.selected;
    if (this.mode === 'sectors') {
      const cards = SECTORS.length;
      if (dx !== 0 && next === cards) {
        this.items[cards]!.side?.(dx);
        return;
      }
      if (dx !== 0 && next < cards) next = Math.max(0, Math.min(cards - 1, next + dx));
      if (dy !== 0) {
        if (next < cards) next = dy > 0 ? cards : cards + 1;
        else if (next === cards) next = dy > 0 ? cards + 1 : this.menus.sectorFocus;
        else next = dy > 0 ? this.menus.sectorFocus : cards;
      }
      if (next < cards) this.menus.sectorFocus = next;
    } else if (dy !== 0) {
      next = (next + dy + this.items.length) % this.items.length;
    } else {
      this.items[next]?.side?.(dx);
      return;
    }
    if (next === this.selected) return;
    this.selected = next;
    sfx.uiMove();
    this.rebuild();
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
    this.confirm.hide();
  }

  /** @returns true while consuming input */
  handleInput(input: InputState): boolean {
    if (!this.open) return false;
    if (this.confirm.handleInput(input)) return true;
    const edge = (v: number, prev: number): number =>
      Math.abs(v) > 0.5 && Math.abs(prev) <= 0.5 ? Math.sign(v) : 0;
    const dx = edge(input.moveX, this.prevMoveX);
    const dy = edge(input.moveY, this.prevMoveY);
    this.prevMoveX = input.moveX;
    this.prevMoveY = input.moveY;
    if (dx !== 0 || dy !== 0) this.navigate(dx, dy);
    if (input.dashPressed) this.activate();
    else if (input.trashPressed) {
      const slot = this.items[this.selected]?.slot;
      if (slot !== undefined && loadSlot(slot) !== null) {
        this.confirm.show(this.screenW, this.screenH, t('confirm.deleteSave'), () => {
          clearSlot(slot);
          this.setMode(this.mode === 'continue' && !this.menus.anySave() ? 'main' : this.mode);
        });
      }
    } else if ((input.pausePressed || input.altPressed) && this.mode !== 'main') {
      sfx.uiMove();
      this.menus.back();
    }
    return true;
  }
}
